import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import type { AddonPricingSummary as AddonPricingItem, AddonPricingModel } from '@lov/shared';

interface AddonPricingSummaryProps {
  selectedAddons: AddonPricingItem[];
  durationHours: number;
}

const SummaryContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: 8,
  border: `1px solid ${theme.palette.divider}`,
}));

export function AddonPricingSummary({ selectedAddons, durationHours }: AddonPricingSummaryProps) {
  const total = selectedAddons.reduce((sum, addon) => sum + addon.subtotal, 0);

  const formatRate = (addon: AddonPricingItem): string => {
    const baseRate = `Rs. ${addon.pricePerUnit.toFixed(2)}`;
    switch (addon.pricingModel) {
      case 'HOURLY' as AddonPricingModel:
        return `${baseRate}/hr × ${addon.durationMultiplier}h`;
      case 'HALF_HOURLY' as AddonPricingModel:
        return `${baseRate}/30min × ${addon.durationMultiplier * 2}`;
      case 'FIXED' as AddonPricingModel:
        return baseRate;
      default:
        return baseRate;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  if (selectedAddons.length === 0) {
    return (
      <SummaryContainer>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Add-Ons Summary
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No add-ons selected
        </Typography>
      </SummaryContainer>
    );
  }

  return (
    <SummaryContainer>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Add-Ons Summary
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Add-on
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Qty
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Rate
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Subtotal
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {selectedAddons.map((addon) => (
            <TableRow key={addon.addonItemId}>
              <TableCell>
                <Typography variant="body2">{addon.addonName}</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">{addon.quantity}</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                  {formatRate(addon)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">{formatCurrency(addon.subtotal)}</Typography>
              </TableCell>
            </TableRow>
          ))}

          <TableRow>
            <TableCell colSpan={4} sx={{ border: 'none', py: 0 }}>
              <Divider sx={{ my: 1 }} />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell colSpan={3}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Add-ons Total
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {formatCurrency(total)}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {durationHours > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          * Duration: {durationHours} {durationHours === 1 ? 'hour' : 'hours'}
        </Typography>
      )}
    </SummaryContainer>
  );
}
